<script setup lang="ts">
import Skills from "../Skills.vue";
import Highlighter from "vue-highlight-words";
import type { ExperienceType, SkillType } from "@/types";
import { computed, useAttrs } from "vue";
import { useSkillsState } from "@/composables/useSkillsState";

/**
 * Props definition
 * Expects `experience` of type `ExperienceType`
 */
export interface Props {
  experience: ExperienceType;
}
const props = defineProps<Props>();
const attrs = useAttrs();
const { selectedSkills } = useSkillsState();

/**
 * Formatting options for dates (e.g., Mar 2023)
 */
const dateOptions: Intl.DateTimeFormatOptions = {
  month: "short",
  year: "numeric",
};

/**
 * Filters skills based on a threshold value
 */
const skillFilter = (skill: SkillType) => skill.value > 89;

/**
 * Calculates the difference between two dates in months
 */
function monthDiff(d1: Date, d2: Date): number {
  let months = (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
  return months <= 0 ? 1 : Math.ceil(months);
}

/**
 * Converts the month difference into a string (e.g., "2 Years 5 Months")
 */
function monthDiffToString(monthsDiff: number): string {
  const years = Math.floor(monthsDiff / 12);
  const months = monthsDiff % 12;
  const yearText = years > 0 ? `${years} Year${years > 1 ? "s" : ""} ` : "";
  const monthText = months > 0 ? `${months} Month${months > 1 ? "s" : ""}` : "";
  return `${yearText}${monthText}`.trim();
}

/**
 * Compute the formatted duration string for the experience;
 * handles Present if no end date is provided.
 */
const formattedDuration = computed(() => {
  const startDate = props.experience.startDate.toLocaleDateString("en-GB", dateOptions);
  const endDate = props.experience.endDate
      ? props.experience.endDate.toLocaleDateString("en-GB", dateOptions)
      : "Present";
  const durationText = monthDiffToString(
      monthDiff(props.experience.startDate, props.experience.endDate || new Date())
  );
  return `${startDate} - ${endDate} (${durationText})`;
});

</script>

<template>
  <!-- Root container -->
  <article v-bind="attrs">
    <!-- Company Header -->
    <div v-if="!experience.grouped" class="cv-company-header relative mb-3 pt-1">
      <span class="absolute -left-[1.57rem] top-2 h-3 w-3 rounded-full border-2 border-white bg-cyan-600 dark:border-slate-900 sm:-left-[2.08rem]" aria-hidden="true"></span>
      <div class="flex items-center gap-3">
        <!-- Company Name -->
        <h3 class="text-lg font-bold text-slate-950 dark:text-white sm:text-xl">{{ experience.company }}</h3>
        <span v-if="experience.industry" class="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold capitalize text-slate-600 dark:bg-slate-800 dark:text-slate-300">{{ experience.industry }}</span>
      </div>
    </div>

    <!-- Experience Details -->
    <div class="cv-experience-details relative mb-9 w-full">
      <!-- Description Container -->
      <div>
        <!-- Title and Final Client -->
        <div class="mb-2 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <h4 class="font-semibold text-slate-900 dark:text-white">
            {{ experience.title }}
            <span v-if="experience.finalClient" class="font-normal text-slate-500 dark:text-slate-400"> · {{ experience.finalClient }}</span>
          </h4>
          <span class="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">{{ formattedDuration }}</span>
        </div>

        <!-- Project Info -->
        <div v-if="experience.project" class="mb-3 text-sm text-slate-500 dark:text-slate-400">
          Project: <span v-html="experience.project" />
        </div>

        <!-- Achievements & Tasks -->
        <div class="mb-3 overflow-hidden">
          <ul class="ml-5 list-disc space-y-2 text-left text-sm leading-6 text-slate-600 marker:text-cyan-600 dark:text-slate-300">
            <!-- Iterate over achievements -->
            <li class="font-medium text-slate-800 dark:text-slate-100"
                v-for="achievement in experience.achievements"
                :key="achievement"
            >
              <Highlighter
                  :highlightClassName="'badge-in-text'"
                  :autoEscape="true"
                  :textToHighlight="achievement"
                  :searchWords="selectedSkills"
              />
            </li>

            <!-- Iterate over tasks -->
            <li v-for="task in experience.tasks" :key="task">
              <Highlighter
                  :highlightClassName="'badge-in-text'"
                  :autoEscape="true"
                  :textToHighlight="task"
                  :searchWords="selectedSkills"
              />
            </li>
          </ul>
        </div>

        <!-- Skills -->
        <div class="mt-4 text-left">
          <Skills
              :skills="experience.skills.filter(skillFilter).map((skill) => skill.label)"
              class="screen-skill"
          />
        </div>
      </div>

      <!-- Image -->
      <img
          v-if="experience.image"
          class="transition transition-all ease-in-out delay-150 brightness-50 h-6 hover:h-64 w-full mx-auto object-cover"
          :src="`/src/assets/${experience.image}`"
          alt="work experience footer image"
          loading="lazy"
          decoding="async"
      />
    </div>
  </article>
</template>
